type AllowedEnv = 'tst' | 'acc' | 'prd'

/**
 * Hosts that resolve to production.
 *
 * A code constant — identical in every build — so a single immutable artifact
 * can serve all three environments and decide its env from the browser's
 * location instead of a build-time `VITE_ENV` value (artifact promotion,
 * FC-157). Excludes Vercel's team-scoped auto-alias
 * (`-suitsupply-team.vercel.app`): it sits behind SSO on this project, so it is
 * not a safe "is this production" signal the way the primary alias is.
 *
 * Includes the custom domain attached to the production Vercel project: a
 * promoted build serves it, and any host missing here silently resolves to
 * 'tst' and books against the testing API.
 */
const PROD_HOSTS = new Set<string>([
  'monorepo-rp-booking-tool.vercel.app',
  'appointment.suitsupply.com',
  'appointment.cn1.suitsupply.com',
])

/**
 * Resolve the runtime environment from a hostname.
 *
 * Order: explicit prod allowlist → acceptance → default 'tst' (the safe
 * choice — a misrouted host never silently behaves as production).
 *
 * PR previews fall through to 'tst' on purpose, unlike the other trunk apps
 * that resolve them to 'prd': this app creates real appointments and sends
 * customer emails, so a preview must never write to production.
 */
export function resolveEnv(hostname?: string | null): AllowedEnv {
  if (!hostname) return 'tst'
  const host = hostname.toLowerCase().split(':')[0]
  if (PROD_HOSTS.has(host)) return 'prd'
  // Anchored to the project-name prefix: a bare 'acceptance' substring would
  // also match preview hosts of branches with 'acceptance' in their name.
  if (
    host.startsWith('monorepo-rp-booking-tool-acceptance') ||
    host === 'acc.appointment.suitsupply.com'
  ) {
    return 'acc'
  }
  return 'tst'
}
